import type { Sprite } from 'pixi.js';
import type { BossDef } from './boss-base';
import type { BulletMotion } from '@/patterns/dsl';

/**
 * Broodworm Queen — the single-giant take on the Twin Serpents.
 * One long worm whose every SEGMENT is a damageable weak point with its OWN
 * health (user request: each part has its own HP and drops by the damage dealt
 * to IT). The armored head takes reduced damage; chew through the glowing
 * joints to shorten and weaken it. It does not lunge (that is the Serpent):
 * it COILS — the head glows, then the body wraps a shrinking ring around
 * where the ship stood — and its joints LAY EGGS that roll to a stop and
 * hatch into spore rings. docs/02 boss pool + B2.
 * Signatures: telegraphed coil + head spore fan + egg-laying joints
 * (always); the tier evolve layers fire from the head.
 */
export const BROODWORM: BossDef = {
  id: 'broodworm',
  nameKey: 'boss.broodworm',
  tier: 4,
  minRoom: 3,
  baseHp: 1700,
  radius: 40,
  bodyKey: 'boss/broodworm/head',
  movement: 'orbit',
  contactDamage: 1,
  phases: [{ hpPct: 1.0, patterns: [] }],
  evolve: [
    {
      // Brood burst: the maw coughs a loose spore ring
      tier: 2,
      pattern: {
        type: 'radial', bullet: 'bullet/enemy/spore', count: 8, interval: 4.4,
        spinDeg: 22, speed: 110, radius: 11, damage: 1,
      },
    },
    {
      // Acid lance: a telegraphed double spit straight at the ship
      tier: 3,
      pattern: {
        type: 'aimed', bullet: 'bullet/enemy/spore', count: 1, spreadDeg: 0,
        interval: 3.6, telegraph: 0.5, burst: 2, burstGap: 0.25,
        speed: 280, radius: 12, damage: 1,
      },
    },
  ],
};

export const SEG_COUNT = 9;
export const SEG_DIST = 40;
export const HEAD_R = 44;
export const SEG_R = 34;
export const HEAD_DMG_MUL = 0.5; // armored maw — chip it or go for the joints
export const PATROL_R = 300;
export const PATROL_SPEED = 0.55;
export const SPEED = 165;
export const STEER = 6;
/** Coil: tell (head glows) → wrap a shrinking ring around the locked spot */
export const COIL = {
  interval: 7.0,
  firstAt: 0.6,
  tell: 0.6,
  tellSlow: 0.35,
  time: 2.8,
  speed: 420,
  steer: 9,
  startR: 340,
  endR: 170,
} as const;
export const FIRE_INTERVAL = 2.2;
/** Joints take turns laying an egg */
export const EGG_INTERVAL = 2.0;
export const EGG_SPEED = 95;
export const EGG_RADIUS = 13;
/** Eggs roll to a stop, then hatch into a spore ring (docs/02 B3 split) */
export const EGG_MOTION: BulletMotion = {
  accel: -150, minSpeed: 0,
  split: {
    after: 1.6, count: 5, spreadDeg: 360, speed: 115, radius: 9, life: 3.5,
    bullet: 'bullet/enemy/spore',
  },
};
export const FAN_WARN = 0.35; // head glows this long before the fan (X13 body cue)
export const FAN_OFFSETS: ReadonlyArray<number> = [-0.26, 0, 0.26];
export const SHOT = { fan: 240 } as const;
export const WARN_TINT = 0xffb07a;
export const COIL_TINT = 0xff6655;
export const HIT_FLASH = 0.08;

export interface Node {
  alive: boolean;
  hp: number;
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  head: boolean;
  sprite: Sprite;
}


// ---- Node-list helpers (no allocation — they walk the fixed node array) ----

/** First living node (the current head). */
export function leadNode(nodes: ReadonlyArray<Node>): Node | null {
  for (const n of nodes) if (n.alive) return n;
  return null;
}

/** Follow-the-leader through the LIVING nodes. */
export function followNodes(nodes: ReadonlyArray<Node>): void {
  let lead: Node | null = null;
  for (const seg of nodes) {
    if (!seg.alive) continue;
    if (lead) {
      seg.prevX = seg.x;
      seg.prevY = seg.y;
      const sx = lead.x - seg.x;
      const sy = lead.y - seg.y;
      const d = Math.hypot(sx, sy);
      if (d > SEG_DIST) {
        const pull = (d - SEG_DIST) / d;
        seg.x += sx * pull;
        seg.y += sy * pull;
      }
    }
    lead = seg;
  }
}

/** Closest living node whose circle the blast touches. */
export function nodeInBlast(
  nodes: ReadonlyArray<Node>, x: number, y: number, radius: number,
): Node | null {
  let best: Node | null = null;
  let bestD = Infinity;
  for (const n of nodes) {
    if (!n.alive) continue;
    const d = Math.hypot(n.x - x, n.y - y);
    if (d < radius + (n.head ? HEAD_R : SEG_R) && d < bestD) {
      bestD = d;
      best = n;
    }
  }
  return best;
}

/** Nearest living soft joint (auto-aim, X7); null when only the head is left. */
export function nearestJoint(nodes: ReadonlyArray<Node>, px: number, py: number): Node | null {
  let best: Node | null = null;
  let bestD = Infinity;
  for (const n of nodes) {
    if (!n.alive || n.head) continue;
    const d = (n.x - px) ** 2 + (n.y - py) ** 2;
    if (d < bestD) {
      bestD = d;
      best = n;
    }
  }
  return best;
}

/** Any living node touching the ship. */
export function nodesTouch(
  nodes: ReadonlyArray<Node>, px: number, py: number, playerRadius: number,
): boolean {
  for (const n of nodes) {
    if (n.alive && Math.hypot(n.x - px, n.y - py) < (n.head ? HEAD_R : SEG_R) + playerRadius) {
      return true;
    }
  }
  return false;
}
